import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  idCardModifyModalRecoil,
} from '@/atom/ModalAtom';
import { placeListRecoil } from '@/atom/PlaceAtom';
import { idCardModifyNumRecoil, idCardModifyRecoil } from '@/atom/IdCardAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import ModalType1 from '@/modal/ModalType1';
import ModifyModal from './ModifyModal';
import { IdCardDto } from '@/model/idCard.dto';
import { PlaceDto } from '@/model/place.dto';

const IdCardModifyModal = () => {
  const navigate = useNavigate();
  const api = useMemo(() => new ApiService(), []);

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [modifyModalState, setModifyModalState] =
    useRecoilState(modifyModalRecoil);
  const [idCardModifyModalState, setIdCardModifyModalState] = useRecoilState(
    idCardModifyModalRecoil,
  );
  const idCardModifyNumState = useRecoilValue(idCardModifyNumRecoil);
  const idCardModifyState = useRecoilValue(idCardModifyRecoil);
  const [placeListState, setPlaceListState] = useRecoilState(placeListRecoil);

  const [applicant, setApplicant] = useState<string>(
    idCardModifyState?.applicant !== undefined
      ? idCardModifyState.applicant
      : '',
  );
  const [phone, setPhone] = useState<string>(
    idCardModifyState?.phone !== undefined ? idCardModifyState.phone : '',
  );
  const [phoneDuplication, setPhoneDuplication] = useState(true);
  const [affiliation, setAffiliation] = useState<string>(
    idCardModifyState?.affiliation !== undefined &&
      idCardModifyState?.affiliation !== 'N/A'
      ? idCardModifyState.affiliation
      : '',
  );
  const [department, setDepartment] = useState<string>(
    idCardModifyState?.department !== undefined &&
      idCardModifyState?.department !== 'Irregular'
      ? idCardModifyState.department
      : '',
  );
  const [grade, setGrade] = useState<string>(
    idCardModifyState?.grade !== undefined &&
      idCardModifyState?.grade !== 'Irregular'
      ? idCardModifyState.grade
      : '',
  );
  const [selectPlaceList, setSelectPlaceList] = useState<PlaceDto[]>([]);
  const [placeList, setPlaceList] = useState<PlaceDto[]>([]);
  const [expireDate, setExpireDate] = useState<string>(
    idCardModifyState?.expire_date !== undefined
      ? idCardModifyState.expire_date
      : '',
  );
  const [firstRender, setFirstRender] = useState<boolean>(false);

  const fetchPlaceList = async () => {
    try {
      const response: PlaceDto[] = await api.getPlaceList(
        String(sessionStorage.getItem('accessToken')),
      );
      if (!firstRender) {
        setSelectPlaceList(
          response.filter((place) =>
            idCardModifyState?.place_id
              .split(',')
              .includes(String(place.place_id)),
          ),
        );
        setFirstRender(true);
      }
      const selectPlaceId = new Set(
        selectPlaceList?.map((selectPlace) => selectPlace.place_id),
      );
      setPlaceList(
        response.filter((place) => !selectPlaceId.has(place.place_id)),
      );
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const handlePhoneDuplication = async () => {
    if (phone.trim() === '') {
      setErrorModalText('핸드폰 번호를 입력해 주세요.');
      return;
    }
    if (phone.trim() !== '') {
      if (!Number(phone)) {
        setErrorModalText('연락처는 숫자만 입력할 수 있습니다.');
        return;
      }
    }
    try {
      await api.getIdCardCheck(
        String(sessionStorage.getItem('accessToken')),
        phone,
      );
      setPhoneDuplication(true);
    } catch (err: any) {
      console.error(err);
      if (err.response.data.message === 'Access Number Already Exist') {
        setErrorModalText('중복된 핸드폰 번호입니다.');
      }
    }
  };

  const handleExpireDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const todayDate = new Date().toISOString().split('T')[0];
    const selectDate = e.target.value;
    if (todayDate >= selectDate) {
      setErrorModalText('만료일은 오늘 혹은 이전 날짜를 선택할 수 없습니다.');
      return;
    }
    setExpireDate(selectDate);
  };

  const handlePlaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectPlace: PlaceDto | undefined = placeList?.find(
      (place) => e.target.value === place.place_id,
    );
    if (selectPlace) {
      setSelectPlaceList((prevList) => [...prevList, selectPlace]);
    }
  };

  const handlePlaceDelete = (placeId: string | undefined) => {
    setSelectPlaceList(
      selectPlaceList.filter((place) => place.place_id !== placeId),
    );
  };

  const handleSubmit = async () => {
    if (
      applicant.trim() === '' ||
      phone.trim() === '' ||
      expireDate.trim() === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    if (!phoneDuplication) {
      setErrorModalText('휴대폰 번호 중복 확인을 해주세요.');
      return;
    }

    const dto: IdCardDto = {
      applicant,
      phone,
      affiliation,
      department,
      grade,
      place_id: selectPlaceList.map((place) => place.place_id).join(','),
      expire_date: expireDate,
    };

    try {
      await api.putIdCardModify(
        String(sessionStorage.getItem('accessToken')),
        Number(idCardModifyNumState),
        dto,
      );
      setCompleteModalState(true);
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPlaceList();
  }, [selectPlaceList]);

  return (
    <>
      <ModalType1>
        <div className="title-box flex aic jcsb">
          <h1>출입신분증 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setIdCardModifyModalState(false)}
          >
            <img src="./img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-wrap">
          <h3>
            <span className="inblock">*</span>필수 항목
          </h3>
          <CS.TableType1_2 className="table-wrap">
            <table>
              <tbody>
                <tr>
                  <th>
                    신청자<span className="star-point">*</span>
                  </th>
                  <td>
                    <IS.TextInputType1
                      type="text"
                      placeholder="신청자"
                      value={applicant}
                      onChange={(e) => setApplicant(e.target.value)}
                    />
                  </td>
                  <th>
                    핸드폰 번호<span className="star-point">*</span>
                  </th>
                  <td>
                    <div className="confirm-btn-box flex aic">
                      <IS.TextInputType1
                        type="text"
                        placeholder="핸드폰 번호"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          setPhoneDuplication(false);
                        }}
                      />
                      <BS.Btn3Type3
                        type="button"
                        onClick={handlePhoneDuplication}
                        disabled={phoneDuplication}
                      >
                        {phoneDuplication ? '완료' : '중복 조회'}
                      </BS.Btn3Type3>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>소속</th>
                  <td>
                    <IS.TextInputType1
                      type="text"
                      placeholder="소속"
                      value={affiliation}
                      onChange={(e) => setAffiliation(e.target.value)}
                    />
                  </td>
                  <th>부서</th>
                  <td>
                    <IS.TextInputType1
                      type="text"
                      placeholder="부서"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <th>직급</th>
                  <td>
                    <IS.TextInputType1
                      type="text"
                      placeholder="직급"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                    />
                  </td>
                  <th>
                    만료일<span className="star-point">*</span>
                  </th>
                  <td>
                    <IS.TextInputType1
                      type="date"
                      value={expireDate}
                      onChange={handleExpireDate}
                    />
                  </td>
                </tr>
                <tr>
                  <th>출입장소</th>
                  <td colSpan={3}>
                    <IS.SelectType1 style={{ width: 180 }}>
                      <select value="default" onChange={handlePlaceChange}>
                        <option disabled={true} value="default">
                          장소
                        </option>
                        {placeList?.map((place) => (
                          <option key={place.id} value={place.place_id}>
                            {place.name}
                          </option>
                        ))}
                      </select>
                      <span className="img-box">
                        <img src="/img/arrow_bottom_555.svg" alt="" />
                      </span>
                    </IS.SelectType1>
                    {selectPlaceList.length !== 0 && (
                      <CS.ListType1>
                        {selectPlaceList.map((place, index) => (
                          <li key={index}>
                            <span className="text">{place.name}</span>
                            <button
                              className="img-box"
                              type="button"
                              onClick={() => handlePlaceDelete(place.place_id)}
                            >
                              <img src="./img/ico_x_fff.svg" alt="" />
                            </button>
                          </li>
                        ))}
                      </CS.ListType1>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </CS.TableType1_2>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setIdCardModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4
              type="button"
              onClick={() => setModifyModalState(true)}
            >
              수정
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
      {modifyModalState && <ModifyModal onConfirm={handleSubmit} />}
    </>
  );
};

export default IdCardModifyModal;
